import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import clsx from "clsx";
import Lucide from "@/components/Base/Lucide";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { getToken } from "@/utils/auth";

type Mode = "add" | "edit";

export default function EditUser(_props: { mode?: Mode }) {
  const params = useParams();
  const id = useMemo(() => String(params.id || "").trim(), [params.id]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState<{ id: string; role: string }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  interface FormData {
    firstName: string;
    lastName: string;
    username: string;
    password: string;
    roleId: string;
    status: string;
    mobileNo: string;
    email: string;
  }

  interface FormErrors {
    firstName?: string;
    lastName?: string;
    username?: string;
    password?: string;
    roleId?: string;
    status?: string;
    email?: string;
  }

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    username: "",
    password: "",
    roleId: "3f2b6c7a-7c2c-4c9e-9f5b-0e3d8c1a4b2f",
    status: "true",
    mobileNo: "",
    email: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setRolesLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
        const token = getToken();
        const res = await fetch(`${apiUrl}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json().catch(() => ({}));
        const data = Array.isArray(body?.data) ? body.data : [];
        if (mounted) setRoles(data);
      } catch (e) {
        console.error("Failed to load roles", e);
      } finally {
        if (mounted) setRolesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (!id) {
        setError("Missing user id.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
        const res = await fetch(`${apiUrl}/users/${id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body?.message || "Failed to load user.");
          return;
        }

        const body = await res.json().catch(() => ({}));
        const u = body?.data || {};

        setFormData((prev) => ({
          ...prev,
          firstName: String(u.firstName || ""),
          lastName: String(u.lastName || ""),
          username: String(u.username || ""),
          email: String(u.email || ""),
          mobileNo: String(u.mobileNo || ""),
          status: u.status === false ? "false" : "true",
          roleId: String(u.roleId || "3f2b6c7a-7c2c-4c9e-9f5b-0e3d8c1a4b2f"),
          password: "",
        }));
      } catch (err) {
        console.error("Load user failed", err);
        setError("Something went wrong loading user.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case "firstName":
        return !value.trim() ? "First name is required" : undefined;
      case "lastName":
        return !value.trim() ? "Last name is required" : undefined;
      case "username":
        if (!value.trim()) return "Username is required";
        if (value.length < 3) return "Username must be at least 3 characters";
        return undefined;
      case "password":
        if (value && value.length < 8) return "Password must be at least 8 characters";
        return undefined;
      case "roleId":
        return !value.trim() ? "Role is required" : undefined;
      case "status":
        return !value.trim() ? "Status is required" : undefined;
      case "email":
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Please enter a valid email address";
        }
        return undefined;
      default:
        return undefined;
    }
  };

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    const requiredFields = ["firstName", "lastName", "username", "roleId", "status"];
    requiredFields.forEach((field) => {
      const err = validateField(field, formData[field as keyof FormData]);
      if (err) errors[field as keyof FormErrors] = err;
    });

    if (formData.password) {
      const err = validateField("password", formData.password);
      if (err) errors.password = err;
    }

    if (formData.email) {
      const err = validateField("email", formData.email);
      if (err) errors.email = err;
    }

    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const togglePasswordVisibility = () => setShowPassword((v) => !v);

  const handleSubmit = async () => {
    try {
      setError("");
      setIsSubmitting(true);

      if (!id) {
        setError("Missing user id.");
        setIsSubmitting(false);
        return;
      }

      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setError("Please enter the required field(s) above before submitting");
        setIsSubmitting(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      const payload: any = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        username: formData.username.trim(),
        email: formData.email.trim(),
        mobileNo: formData.mobileNo.trim() || undefined,
        roleId: formData.roleId.trim() || undefined,
        status: formData.status,
      };
      if (formData.password) payload.password = formData.password;

      const res = await fetch(`${apiUrl}/users/${id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message || "Submission failed");
        return;
      }

      navigate("/users");
    } catch (err) {
      console.error("Update user failed", err);
      setError("Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return <div className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</div>;
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
          <div className="col-span-12 sm:col-span-10 sm:col-start-2">
            <div className="flex flex-col lg:items-center lg:flex-row gap-y-2">
              <div
                className={clsx([
                  "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group active",
                  "after:hidden before:hidden after:lg:block before:lg:block",
                  "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                  "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                  "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                  "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                ])}
              >
                <div className="flex items-center">
                  <div className="bg-white border rounded-full group-[.mode--light]:!bg-transparent group-[.active]:bg-primary group-[.active]:text-white group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-white/[0.25] [.group.mode--light_.group.active_&]:!bg-white/[0.12] [.group.mode--light_.group.active_&]:!border-white/[0.15] dark:[.group.mode--light_.group.active_&]:!bg-darkmode-800/30 dark:[.group.mode--light_.group.active_&]:!border-white/10 dark:group-[.mode--light]:!border-white/10 dark:bg-transparent">
                    <div className="flex items-center justify-center w-10 h-10">1</div>
                  </div>
                  <div className="ml-3.5 group-[.mode--light]:!text-slate-300 font-medium whitespace-nowrap text-slate-500 group-[.active]:text-current [.group.mode--light_.group.active_&]:!text-slate-100">
                    Edit User Information
                  </div>
                </div>
              </div>
              <div
                className={clsx([
                  "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group",
                  "after:hidden before:hidden after:lg:block before:lg:block",
                  "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                  "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                  "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                  "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                ])}
              />
            </div>

            <div className="mt-7 space-y-6">
              {loading ? (
                <div className="text-slate-500">Loading...</div>
              ) : (
                <>
                  {/* Container 1: Mandatory Fields */}
                  <div className="flex flex-col box box--stacked">
                    <div className="p-7">
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                          Required Information
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          Please fill in all required fields marked with *
                        </p>
                      </div>

                      {/* === First Name === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">First Name</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Required
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormInput
                            type="text"
                            name="firstName"
                            required
                            placeholder="Enter first name"
                            value={formData.firstName}
                            onChange={handleChange}
                            disabled={loading || isSubmitting}
                            className={clsx({
                              "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.firstName,
                            })}
                          />
                          <FieldError error={formErrors.firstName} />
                        </div>
                      </div>

                      {/* === Last Name === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Last Name</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Required
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormInput
                            type="text"
                            name="lastName"
                            required
                            placeholder="Enter last name"
                            value={formData.lastName}
                            onChange={handleChange}
                            disabled={loading || isSubmitting}
                            className={clsx({
                              "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.lastName,
                            })}
                          />
                          <FieldError error={formErrors.lastName} />
                        </div>
                      </div>

                      {/* === Username === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Username</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Required
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormInput
                            type="text"
                            name="username"
                            required
                            placeholder="Enter username"
                            value={formData.username}
                            onChange={handleChange}
                            disabled={loading || isSubmitting}
                            className={clsx({
                              "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.username,
                            })}
                          />
                          <FieldError error={formErrors.username} />
                        </div>
                      </div>

                      {/* === Password === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Password</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Optional
                              </div>
                            </div>
                            <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                              Leave blank to keep current password
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <div className="relative">
                            <FormInput
                              type={showPassword ? "text" : "password"}
                              name="password"
                              placeholder="Leave blank to keep current"
                              value={formData.password}
                              onChange={handleChange}
                              disabled={loading || isSubmitting}
                              className={clsx({
                                "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.password,
                                "pr-10": true,
                              })}
                            />
                            <button
                              type="button"
                              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                              onClick={togglePasswordVisibility}
                              disabled={loading || isSubmitting}
                            >
                              <Lucide icon={showPassword ? "EyeOff" : "Eye"} className="w-4 h-4" />
                            </button>
                          </div>
                          <FieldError error={formErrors.password} />
                        </div>
                      </div>

                      {/* === Role === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Role</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Required
                              </div>
                            </div>
                            <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                              Select the user's role (e.g. ADMIN / APP).
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormSelect
                            name="roleId"
                            required
                            value={formData.roleId}
                            onChange={handleChange}
                            disabled={loading || isSubmitting || rolesLoading}
                            className={clsx({
                              "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.roleId,
                            })}
                          >
                            <option value="">{rolesLoading ? "Loading roles..." : "Select a role"}</option>
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.role}
                              </option>
                            ))}
                          </FormSelect>
                          <FieldError error={formErrors.roleId} />
                        </div>
                      </div>

                      {/* === Status === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Status</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Required
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormSelect
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            required
                            disabled={loading || isSubmitting}
                            className={clsx({
                              "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.status,
                            })}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </FormSelect>
                          <FieldError error={formErrors.status} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Container 2: Optional Fields */}
                  <div className="flex flex-col box box--stacked">
                    <div className="p-7">
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                          Additional Information
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          Optional fields to provide more details about the user
                        </p>
                      </div>

                      {/* === Mobile Number === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Mobile Number</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Optional
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormInput
                            type="tel"
                            name="mobileNo"
                            placeholder="e.g., 012-3456789"
                            value={formData.mobileNo}
                            onChange={handleChange}
                            disabled={loading || isSubmitting}
                          />
                        </div>
                      </div>

                      {/* === Email === */}
                      <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                        <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                          <div className="text-left">
                            <div className="flex items-center">
                              <div className="font-medium">Email</div>
                              <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                Optional
                              </div>
                            </div>
                          </div>
                        </label>
                        <div className="flex-1 w-full mt-3 xl:mt-0">
                          <FormInput
                            type="email"
                            name="email"
                            placeholder="e.g., user@example.com"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={loading || isSubmitting}
                            className={clsx({
                              "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.email,
                            })}
                          />
                          <FieldError error={formErrors.email} />
                        </div>
                      </div>

                      {error && <div className="mt-6 text-danger text-sm">{error}</div>}
                    </div>

                    <div className="flex py-5 border-t md:justify-end px-7 border-slate-200/80">
                      <Button
                        variant="outline-primary"
                        className="w-full px-10 md:w-auto border-primary/50"
                        onClick={handleSubmit}
                        disabled={loading || isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Lucide
                              icon="Loader2"
                              className="stroke-[1.3] w-4 h-4 mr-2 -ml-2 animate-spin"
                            />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Lucide icon="Pocket" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}

